import React from 'react';
import { CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const QualityManagementPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <section className="relative h-80 flex items-center justify-center overflow-hidden bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Quality Management Services</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Technical integrity, regulatory compliance, and continuous improvement
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Quality Management Services for <span className="text-red-600">Oil & Gas Projects</span>
            </h2>
            <div className="w-24 h-1 bg-red-600 mb-8" />
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Quality management in the oil and gas sector is essential to ensuring safe operations, regulatory compliance, and long term asset reliability. Total Vision Oil and Gas Fields Services and Consultancy LLC SPC provides comprehensive quality management services that support every phase of the project lifecycle—from design and procurement to construction, commissioning, and handover. Our approach integrates international standards, disciplined processes, and experienced quality professionals to deliver projects that meet the highest performance expectations.
            </p>
          </div>

          {/* Quality Planning */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Quality Planning and System Development</h3>
            <p className="text-slate-700 mb-6">
              A strong quality foundation begins with well defined systems and procedures.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Development of project-specific Quality Management Plans (QMP)',
                'Preparation of Inspection & Test Plans (ITPs), Method Statements, and Quality Control Procedures',
                'Establishment of document control and traceability systems'
              ].map((item, idx) => (
                <Card key={idx} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-slate-700 mt-6 italic">
              Total Vision Oil and Gas Fields Services and Consultancy LLC SPC plays a key role in early-stage quality planning, ensuring that all engineering and construction activities are governed by robust, auditable processes.
            </p>
          </div>

          {/* Vendor Quality Surveillance */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Vendor Quality Surveillance and Procurement Quality Assurance</h3>
            <p className="text-slate-700 mb-6">
              Procurement quality is critical in oil and gas projects due to long lead equipment and strict performance requirements.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Vendor audits, capability assessments, and pre qualification',
                'Review of vendor quality plans, procedures, and manufacturing records',
                'Witnessing of inspections, FATs, and performance tests',
                'Monitoring of welding, fabrication, coating, and assembly processes'
              ].map((item, idx) => (
                <Card key={idx} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-slate-700 mt-6 italic">
              Total Vision ensures that all procured materials and equipment meet the required technical and quality standards before reaching the site.
            </p>
          </div>

          {/* Construction Quality Control */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Construction Quality Control and Field Inspection</h3>
            <p className="text-slate-700 mb-6">
              Quality control during construction ensures that installations meet design intent and regulatory requirements.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'On-site inspection of civil, structural, mechanical, piping, electrical, and instrumentation works',
                'Welding inspection, NDT coordination, hydrotesting, and pressure testing',
                'Verification of material certificates, calibration records, and equipment compliance',
                'Monitoring of construction activities against ITPs and approved drawings',
                'Identification and closure of NCRs, punch items, and quality deviations'
              ].map((item, idx) => (
                <Card key={idx} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-slate-700 mt-6 italic">
              Total Vision provides experienced QC inspectors and quality engineers who maintain strict oversight of field execution.
            </p>
          </div>

          {/* Quality Assurance */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Quality Assurance and Compliance Monitoring</h3>
            <p className="text-slate-700 mb-6">
              Quality assurance ensures that processes—not just products—meet required standards.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Internal audits, surveillance visits, and compliance reviews',
                'Monitoring of contractor performance and adherence to QMS requirements',
                'Root cause analysis and implementation of corrective and preventive actions (CAPA)',
                'Continuous improvement programs and lessons learned integration',
                'Reporting of KPIs, quality metrics, and performance dashboards'
              ].map((item, idx) => (
                <Card key={idx} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-slate-700 mt-6 italic">
              Total Vision strengthens client confidence by ensuring that all project activities follow approved procedures and industry best practices.
            </p>
          </div>

          {/* HSE and Regulatory Integration */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">HSE and Regulatory Integration</h3>
            <p className="text-slate-700 mb-6">
              Quality and safety are closely linked in oil and gas operations.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Integration of quality processes with HSE management systems',
                'Compliance with ADNOC, Aramco, OSHA, and local regulatory frameworks',
                'Verification of fire protection, hazardous area compliance, and safety critical equipment',
                'Support for regulatory inspections and third party certifications'
              ].map((item, idx) => (
                <Card key={idx} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-slate-700 mt-6 italic">
              Total Vision ensures that quality management supports safe, compliant, and sustainable project execution.
            </p>
          </div>

          {/* Pre Commissioning */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Pre Commissioning, Commissioning, and Handover Quality</h3>
            <p className="text-slate-700 mb-6">
              The final project stages require meticulous verification and documentation.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'System walkdowns, punch list management, and mechanical completion verification',
                'Review of test packs, loop checks, and functional testing records',
                'Compilation of as built documentation, MDRs, and final quality dossiers',
                'Support for commissioning teams to ensure readiness for start up'
              ].map((item, idx) => (
                <Card key={idx} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-slate-700 mt-6 italic">
              Total Vision ensures a smooth transition from construction to operations, backed by complete and compliant quality documentation.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-slate-900 mb-6">Why Quality Management Matters in Oil & Gas</h3>
            <p className="text-lg text-slate-700 leading-relaxed mb-8">
              Effective quality management reduces rework, enhances safety, and ensures long term asset performance. With a disciplined approach and deep technical expertise, Total Vision Oil and Gas Fields Services and Consultancy LLC SPC ensures:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                'Compliance with international and client-specific standards',
                'Reduced project risks and improved predictability',
                'High-quality engineering and construction deliverables',
                'Strong vendor and contractor performance',
                'Reliable commissioning and operational readiness'
              ].map((item, idx) => (
                <Card key={idx} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QualityManagementPage;